import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { RouterProvider } from 'react-router';
import { router } from './routes';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { pushNotificationService } from './services/pushNotificationService';
import { hydrateDriverMobileSession } from './utils/driverMobileSession';
import { AppAlertProvider, useAppAlert } from './components/AppAlertProvider';
import { Toaster } from 'sonner';
import { settingsService } from './services/settingsService';
import { appUpdateService } from './services/appUpdateService';
import { getStoredThemePreference, getThemeWrapperClass, getUserThemePreferenceSettingKey, setStoredThemePreference, THEME_PREFERENCE_EVENT, type ThemePreference } from './utils/themePreference';
import { isAnyDriverUser } from './utils/userRoles';
import { webAppVersionService, type WebAppVersionInfo } from './services/webAppVersionService';

const WEB_UPDATE_CHECK_INTERVAL_MS = 60000;
const WEB_UPDATE_RELOAD_DELAY_SECONDS = 10;

function WebAppUpdateChecker() {
  const [pendingUpdate, setPendingUpdate] = useState<WebAppVersionInfo | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(WEB_UPDATE_RELOAD_DELAY_SECONDS);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: number | undefined;

    const checkUpdate = async () => {
      try {
        const update = await webAppVersionService.checkForUpdate();
        if (cancelled || !update) return;
        const promptKey = `web-update-prompt:${update.buildId}`;
        if (sessionStorage.getItem(promptKey) === 'shown') return;
        sessionStorage.setItem(promptKey, 'shown');
        setPendingUpdate(update);
        setSecondsLeft(WEB_UPDATE_RELOAD_DELAY_SECONDS);
      } catch (error) {
        console.warn('Web update check failed (non-blocking):', error);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkUpdate();
      }
    };

    void checkUpdate();
    pollTimer = window.setInterval(() => void checkUpdate(), WEB_UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onVisibilityChange);

    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!pendingUpdate) return;
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          webAppVersionService.forceReload(pendingUpdate.buildId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [pendingUpdate?.buildId]);

  if (!pendingUpdate) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
          <RefreshCw className="h-6 w-6" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Yeni Sürüm Yayınlandı</h2>
        <p className="mt-2 text-sm text-gray-600">
          Yeni bir sürüm mevcut. Sayfa {secondsLeft} saniye içinde otomatik olarak yenilenecek.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => webAppVersionService.forceReload(pendingUpdate.buildId)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Şimdi Yenile
          </button>
        </div>
      </div>
    </div>
  );
}

function MobileUpdateChecker() {
  const { showConfirm } = useAppAlert();

  useEffect(() => {
    let cancelled = false;

    const checkUpdate = async () => {
      try {
        const result = await appUpdateService.checkForUpdate();
        if (cancelled || !result?.updateAvailable) return;

        const promptKey = `mobile-update-prompt:${result.platform}:${result.latestVersion}`;
        if (!result.forceUpdate && sessionStorage.getItem(promptKey) === 'shown') return;
        sessionStorage.setItem(promptKey, 'shown');

        const shouldOpenStore = await showConfirm({
          title: 'Güncelleme Gerekli',
          message: `${result.message}\n\nMevcut sürüm: ${result.currentVersion}\nMarket sürümü: ${result.latestVersion}`,
          confirmText: 'Güncelle',
          cancelText: result.forceUpdate ? 'Daha Sonra' : 'Kapat',
          tone: 'warning',
        });

        if (shouldOpenStore) {
          appUpdateService.openStore(result.storeUrl);
        }
      } catch (error) {
        console.warn('Mobile update check failed (non-blocking):', error);
      }
    };

    void checkUpdate();

    return () => {
      cancelled = true;
    };
  }, [showConfirm]);

  return null;
}

function ThemeAwareShell() {
  const { user } = useAuth();
  const isDriverUser = isAnyDriverUser(user);
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => getStoredThemePreference());

  useEffect(() => {
    const syncTheme = () => {
      if (!user?.id) {
        setThemePreferenceState(getStoredThemePreference());
        return;
      }
      const storedTheme = localStorage.getItem(`ui-theme-preference:${user.id}`);
      if (!storedTheme && isDriverUser) {
        setStoredThemePreference('light', user.id);
        setThemePreferenceState('light');
        return;
      }
      setThemePreferenceState(getStoredThemePreference(user?.id));
    };
    syncTheme();

    if (!user?.id) return;

    settingsService.getMySettingValue<ThemePreference>(
      getUserThemePreferenceSettingKey(user.id),
      isDriverUser ? 'light' : getStoredThemePreference(user.id),
    ).then((storedTheme) => {
      const nextTheme = storedTheme === 'light' ? 'light' : (isDriverUser ? 'light' : 'dark');
      setStoredThemePreference(nextTheme, user.id);
      setThemePreferenceState(nextTheme);
    }).catch(() => {
      syncTheme();
    });
  }, [isDriverUser, user?.id]);

  useEffect(() => {
    const syncTheme = () => setThemePreferenceState(getStoredThemePreference(user?.id));
    const handleThemeChange = () => syncTheme();
    syncTheme();
    window.addEventListener(THEME_PREFERENCE_EVENT, handleThemeChange as EventListener);
    window.addEventListener('storage', handleThemeChange);
    return () => {
      window.removeEventListener(THEME_PREFERENCE_EVENT, handleThemeChange as EventListener);
      window.removeEventListener('storage', handleThemeChange);
    };
  }, [user?.id]);

  useEffect(() => {
    const themeClass = getThemeWrapperClass(themePreference);
    document.documentElement.classList.remove('theme-dark-orange', 'theme-light');
    document.body.classList.remove('theme-dark-orange', 'theme-light');
    document.documentElement.classList.add(themeClass);
    document.body.classList.add(themeClass);
  }, [themePreference]);

  useEffect(() => {
    let activePanel: HTMLElement | null = null;
    let offsetX = 0;
    let offsetY = 0;

    const isInteractive = (target: EventTarget | null) => {
      return target instanceof HTMLElement
        && Boolean(target.closest('button, input, textarea, select, a, label, [role="button"], [data-no-drag]'));
    };

    const isOverlay = (element: Element | null) => {
      return element instanceof HTMLElement
        && element.classList.contains('fixed')
        && element.classList.contains('inset-0')
        && element.classList.contains('z-50');
    };

    const resolvePanel = (target: HTMLElement | null) => {
      if (!target) return null;
      const slotPanel = target.closest('[data-slot="dialog-content"], [data-slot="alert-dialog-content"]');
      if (slotPanel instanceof HTMLElement) return slotPanel;

      let current: HTMLElement | null = target;
      while (current?.parentElement) {
        if (isOverlay(current.parentElement)) return current;
        current = current.parentElement;
      }
      return null;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!activePanel) return;
      activePanel.style.left = `${event.clientX - offsetX}px`;
      activePanel.style.top = `${event.clientY - offsetY}px`;
    };

    const stopDragging = () => {
      activePanel = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDragging);
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target || isInteractive(target)) return;

      const panel = resolvePanel(target);
      if (!panel) return;

      const rect = panel.getBoundingClientRect();
      const clickedTopArea = event.clientY - rect.top <= 64;
      if (!clickedTopArea) return;

      activePanel = panel;
      offsetX = event.clientX - rect.left;
      offsetY = event.clientY - rect.top;

      activePanel.style.position = 'fixed';
      activePanel.style.left = `${rect.left}px`;
      activePanel.style.top = `${rect.top}px`;
      activePanel.style.width = `${rect.width}px`;
      activePanel.style.maxWidth = `${rect.width}px`;
      activePanel.style.transform = 'none';
      activePanel.style.margin = '0';

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopDragging);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      stopDragging();
    };
  }, []);

  return (
    <div className={`${getThemeWrapperClass(themePreference)} min-h-[100dvh]`}>
      <MobileUpdateChecker />
      <WebAppUpdateChecker />
      <RouterProvider router={router} />
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}

function App() {
  hydrateDriverMobileSession();

  useEffect(() => {
    pushNotificationService.init().catch((error) => {
      console.warn('Push init promise rejected (non-blocking):', error);
    });
  }, []);

  return (
    <AuthProvider>
      <AppAlertProvider>
        <ThemeAwareShell />
      </AppAlertProvider>
    </AuthProvider>
  );
}

export default App;
